import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { avanzarMesEnTexto, can, periodoPorDefectoCxc, siguientePeriodo, type MesCxc, type PeriodoCxc, type Rol } from '@leadsphere/types';

import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CheckboxDropdown } from '../../components/ui/CheckboxDropdown.js';
import { ConfirmModal } from '../../components/ui/ConfirmModal.js';
import { ExpandedRow, ExpField } from '../../components/ui/ExpandedRow.js';
import { Pagination } from '../../components/ui/Pagination.js';
import { useApi } from '../../hooks/useApi.js';
import { useClientesLookup } from '../../hooks/useClientesLookup.js';
import { useAuth } from '../../hooks/useAuth.js';
import { ApiError, descargarArchivo } from '../../lib/api.js';
import { fmtFechaDisplay } from '../../lib/dates.js';
import { traerTodasLasPaginas } from '../../lib/paginas.js';
import { NuevaEntradaModal } from './NuevaEntradaModal.js';
import type { EntradaCxc, EntradaPrefill } from './types.js';

const ROLES_VALIDOS: readonly string[] = ['root', 'head', 'analista', 'kam', 'ads_manager', 'gerente_comercial', 'comercial', 'digital', 'CEO'];

// Entrada abierta en el modal: crear (sin id) o editar una entrada existente —
// una sola instancia de NuevaEntradaModal para ambos modos (F.6.2).
type EntradaModalState =
  | { modo: 'crear' }
  | { modo: 'editar'; id: string }
  | { modo: 'duplicar'; prefill: EntradaPrefill }
  | null;

// Duplicar (mejoras CxC 2026-09-30): solo lo recurrente, al mes siguiente, y
// el mes que aparezca en el Concepto también avanza.
function prefillDuplicado(e: EntradaCxc): EntradaPrefill {
  const { mes, anio } = siguientePeriodo(e.mes, e.anio);
  return {
    cliente_id: e.cliente_id,
    concepto: e.concepto ? avanzarMesEnTexto(e.concepto) : '',
    es_adicional: e.es_adicional,
    mes,
    anio,
    monto: e.monto,
    plazo_dias: e.plazo_dias,
  };
}

function DuplicarIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// #75: fees de servicio vs. adicionales (refacturación, comisiones…).
const TIPO_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'servicio', label: 'Servicio' },
  { value: 'adicional', label: 'Adicional' },
];

const ESTADO_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'Vigente', label: 'Vigente' },
  { value: 'VENCIDA', label: 'Vencida' },
  { value: 'Pagada', label: 'Pagada' },
];

// Labels de autorizacion_factura para mostrar en la tabla (texto plano) —
// misma lista que NuevaEntradaModal.tsx, no se importa de ahí para no tocar ese archivo.
const AUTORIZACION_LABEL: Record<string, string> = {
  solicitar_factura: 'Solicitar factura',
  factura_solicitada: 'Factura solicitada',
  factura_cancelada: 'Factura cancelada',
  nota_de_credito: 'Nota de crédito',
  esperar_autorizacion: 'Esperar autorización',
  factura_enviada: 'Factura enviada',
  factura_vigente: 'Factura vigente',
  pago_atrasado: 'Pago atrasado',
};

const ALERTA_CLASS: Record<number, string> = { 1: 'a30', 2: 'a60', 3: 'a90', 4: 'a90p' };

const LIMIT = 25;

function fmtMXN(value: string | number): string {
  const n = typeof value === 'string' ? Number(value) : value;
  return `$${n.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// pago==='Si' se muestra como "Pagada" aunque estado (calculado en backend) sea null
// para entradas pagadas — mismo criterio que apps/api/.../cxc.service.ts calcularCampos().
function estadoCalculado(e: EntradaCxc): 'Pagada' | 'VENCIDA' | 'Vigente' | null {
  if (e.pago === 'Si') return 'Pagada';
  return e.estado;
}

function EstadoBadge({ entrada }: { entrada: EntradaCxc }) {
  const estado = estadoCalculado(entrada);
  if (estado === 'Pagada') return <Badge tone="info">Pagada</Badge>;
  if (estado === 'VENCIDA') return <Badge tone="danger">Vencida</Badge>;
  if (estado === 'Vigente') return <Badge tone="success">Vigente</Badge>;
  return <span style={{ color: 'var(--muted)', fontSize: 12 }}>—</span>;
}

function PagoIndicator({ pago }: { pago: EntradaCxc['pago'] }) {
  if (pago === 'Si') return <span style={{ color: 'var(--success)', fontWeight: 600 }}>✓ Sí</span>;
  if (pago === 'No') return <span style={{ color: 'var(--danger)', fontWeight: 600 }}>✗ No</span>;
  return <span style={{ color: 'var(--muted)' }}>—</span>;
}

export function CxCPage() {
  const { post } = useApi();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const rol = user?.rol && ROLES_VALIDOS.includes(user.rol) ? (user.rol as Rol) : null;
  // Solo root y gerente_comercial escriben en CxC (decisión 15-oct; comercial
  // pasó a solo lectura) — canWriteCxC ya refleja la regla.
  const puedeEditarCxC = rol !== null && can(rol, 'canWriteCxC');

  const [search, setSearch] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // Desde Reportes → "A quién cobrar": /cxc?cliente=<id> abre CxC con ese
  // cliente y TODOS los periodos (si no, su deuda vieja quedaría escondida).
  const clienteDeUrl = searchParams.get('cliente');
  // Filtros de selección múltiple (Moisés, 14-oct); vacío = sin filtro. Año y
  // Mes en null = el periodo por defecto, el mismo criterio que Reportes.
  const [anioFiltro, setAnioFiltro] = useState<string[] | null>(clienteDeUrl ? [] : null);
  const [mesFiltro, setMesFiltro] = useState<string[] | null>(clienteDeUrl ? [] : null);
  const [estadoFiltro, setEstadoFiltro] = useState<string[]>([]);
  const [clienteFiltro, setClienteFiltro] = useState<string[]>(clienteDeUrl ? [clienteDeUrl] : []);
  const [tipoFiltro, setTipoFiltro] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [entradaModal, setEntradaModal] = useState<EntradaModalState>(null);
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const [accionError, setAccionError] = useState('');
  const [cancelarPendiente, setCancelarPendiente] = useState<string | null>(null);

  const { data: clientesData } = useClientesLookup();

  const clienteNombre = useMemo(() => {
    const map: Record<string, string> = {};
    for (const c of clientesData?.data ?? []) map[c.id] = c.nombre_comercial;
    return map;
  }, [clientesData]);

  // Los campos calculados (vencimiento, estado, alerta, *_con_iva) no son columnas
  // reales en BD — se calculan en cxc.service.ts al leer, así que no se pueden filtrar
  // ni paginar en el servidor. Se traen TODAS las páginas (tope de 100 por página que
  // impone el backend, ver lib/paginas.ts) y se filtra/pagina aquí.
  //
  // Hotfix "días vencidos no se recalculan": el backend SÍ recalcula en vivo en
  // cada request (verificado) — lo que quedaba "atorado" era esta query, que
  // heredaba los defaults globales (queryClient.ts): refetchOnWindowFocus:false
  // y staleTime de 1 min. dias_postvencimiento/alerta cambian por DÍA (no por
  // sesión ni por mutación), así que dejar la pestaña abierta y "volver" a
  // ella nunca disparaba un refetch — solo lo forzaba una edición (invalidate
  // manual). Se sobreescribe aquí, solo para esta query, sin tocar el default
  // global (el resto de la app sigue sin refetch-on-focus).
  const { data: cxcData, isLoading } = useQuery({
    queryKey: ['cxc', 'todas'],
    queryFn: () => traerTodasLasPaginas<EntradaCxc>('/cxc'),
    staleTime: 60_000,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  const todasLasEntradas = useMemo(() => cxcData?.data ?? [], [cxcData]);

  const aniosDisponibles = useMemo(
    () => [...new Set(todasLasEntradas.map(e => e.anio))].sort((a, b) => b - a),
    [todasLasEntradas],
  );

  // Periodo por defecto: mes y año actuales (CDMX) o los últimos con registros.
  const periodoCalculado = useMemo(
    () => periodoPorDefectoCxc(
      todasLasEntradas.map(e => ({ anio: e.anio, mes: e.mes as MesCxc })),
      new Date().toLocaleDateString('en-CA', { timeZone: 'America/Mexico_City' }),
    ),
    [todasLasEntradas],
  );
  // Se fija con la primera carga: capturar la primera entrada del mes actual
  // haría saltar la vista del mes anterior al actual. "Limpiar" lo recalcula.
  const [periodoFijado, setPeriodoFijado] = useState<PeriodoCxc | null>(null);
  useEffect(() => {
    if (periodoFijado === null && periodoCalculado) setPeriodoFijado(periodoCalculado);
  }, [periodoFijado, periodoCalculado]);
  const periodoDefecto = periodoFijado ?? periodoCalculado;
  const aniosAplicados = useMemo(
    () => anioFiltro ?? (periodoDefecto ? [String(periodoDefecto.anio)] : []),
    [anioFiltro, periodoDefecto],
  );
  const mesesAplicados = useMemo(
    () => mesFiltro ?? (periodoDefecto ? [periodoDefecto.mes] : []),
    [mesFiltro, periodoDefecto],
  );

  // Todos los filtros menos Año y Mes: base de las tarjetas "al día de hoy".
  const entradasSinPeriodo = useMemo(() => {
    const s = search.trim().toLowerCase();
    return todasLasEntradas.filter(e => {
      if (s) {
        const nombre = (clienteNombre[e.cliente_id] ?? '').toLowerCase();
        const numFactura = (e.numero_factura ?? '').toLowerCase();
        if (!nombre.includes(s) && !numFactura.includes(s)) return false;
      }
      const estado = estadoCalculado(e);
      if (estadoFiltro.length && !(estado && estadoFiltro.includes(estado))) return false;
      if (clienteFiltro.length && !clienteFiltro.includes(e.cliente_id)) return false;
      if (tipoFiltro.length && !tipoFiltro.includes(e.es_adicional ? 'adicional' : 'servicio')) return false;
      return true;
    });
  }, [todasLasEntradas, search, estadoFiltro, clienteFiltro, tipoFiltro, clienteNombre]);

  const entradasFiltradas = useMemo(
    () => entradasSinPeriodo.filter(e =>
      (!aniosAplicados.length || aniosAplicados.includes(String(e.anio)))
      && (!mesesAplicados.length || mesesAplicados.includes(e.mes))),
    [entradasSinPeriodo, aniosAplicados, mesesAplicados],
  );

  // Tarjetas (Moisés, 14-oct). Facturado, Cobrado y Por cobrar son "del
  // periodo": siguen todos los filtros. Deuda vencida y Por vencer son la foto
  // "al día de hoy" de todos los meses: con el filtro por defecto (el mes
  // actual) casi nada está vencido aún, y una "Deuda vencida: $0" engañaría.
  const stats = useMemo(() => {
    const hoy = new Date();
    const hoyISO = hoy.toISOString().slice(0, 10);
    const en30ISO = new Date(hoy.getTime() + 30 * 86_400_000).toISOString().slice(0, 10);

    let totalFacturado = 0;
    let facturasActivas = 0;
    let deudaVencida = 0;
    let facturasEnMora = 0;
    let cobrado = 0;
    let facturasLiquidadas = 0;
    let porVencer = 0;
    let facturasPorVencer = 0;
    let porCobrar = 0;
    let facturasPorCobrar = 0;

    for (const e of entradasSinPeriodo) {
      if (e.estado === 'VENCIDA') {
        deudaVencida += Number(e.vencido_con_iva);
        facturasEnMora += 1;
      }

      if (e.pago !== 'Si' && e.vencimiento && e.vencimiento >= hoyISO && e.vencimiento <= en30ISO) {
        porVencer += Number(e.facturado_con_iva);
        facturasPorVencer += 1;
      }
    }

    for (const e of entradasFiltradas) {
      totalFacturado += Number(e.facturado_con_iva);
      if (e.estado_factura !== 'cancelada') facturasActivas += 1;

      if (e.pago === 'Si') {
        cobrado += Number(e.cobrado_con_iva);
        facturasLiquidadas += 1;
      }

      if (Number(e.por_cobrar_con_iva) > 0) {
        porCobrar += Number(e.por_cobrar_con_iva);
        facturasPorCobrar += 1;
      }
    }

    return {
      totalFacturado, facturasActivas, deudaVencida, facturasEnMora, cobrado, facturasLiquidadas,
      porVencer, facturasPorVencer, porCobrar, facturasPorCobrar,
    };
  }, [entradasFiltradas, entradasSinPeriodo]);

  const totalPages = Math.max(1, Math.ceil(entradasFiltradas.length / LIMIT));
  const entradasPagina = entradasFiltradas.slice((page - 1) * LIMIT, page * LIMIT);
  const hasFilters = Boolean(
    search || anioFiltro !== null || mesFiltro !== null || estadoFiltro.length || clienteFiltro.length || tipoFiltro.length,
  );

  // Limpiar vuelve al periodo por defecto (recalculado), no a "todo". También
  // quita el ?cliente= de Reportes, para que al recargar no vuelva el filtro.
  const resetFiltros = () => {
    setPeriodoFijado(periodoCalculado);
    if (clienteDeUrl) setSearchParams({}, { replace: true });
    setSearch('');
    setAnioFiltro(null);
    setMesFiltro(null);
    setEstadoFiltro([]);
    setClienteFiltro([]);
    setTipoFiltro([]);
    setPage(1);
  };

  const solicitarMutation = useMutation({
    mutationFn: (id: string) => post(`/cxc/${id}/solicitar-factura`),
    onSuccess: () => {
      setAccionError('');
      queryClient.invalidateQueries({ queryKey: ['cxc'] });
    },
    onError: (err: unknown) => {
      setAccionError(err instanceof ApiError ? err.message : 'No fue posible solicitar la factura.');
    },
  });

  const cancelarMutation = useMutation({
    mutationFn: (id: string) => post(`/cxc/${id}/cancelar-factura`),
    onSuccess: () => {
      setAccionError('');
      queryClient.invalidateQueries({ queryKey: ['cxc'] });
    },
    onError: (err: unknown) => {
      setAccionError(err instanceof ApiError ? err.message : 'No fue posible cancelar la factura.');
    },
  });

  function handleCancelar(id: string) {
    setCancelarPendiente(id);
  }

  // window.open(url) directo no manda el header Authorization -> 401. Mismo
  // patrón que TicketDetalle.tsx (descargarArchivo: fetch autenticado + blob).
  async function handleDescargarFactura(e: EntradaCxc, tipo: 'xml' | 'pdf') {
    const url = tipo === 'xml' ? e.xml_url : e.pdf_url;
    if (!url) return;
    const nombreOriginal = decodeURIComponent(url.split('/').pop() ?? `factura.${tipo}`);
    try {
      await descargarArchivo(`/cxc/${e.id}/${tipo}`, nombreOriginal);
    } catch (err) {
      setAccionError(err instanceof ApiError ? err.message : 'No fue posible descargar el archivo.');
    }
  }

  function confirmarCancelar() {
    if (cancelarPendiente) cancelarMutation.mutate(cancelarPendiente);
    setCancelarPendiente(null);
  }

  function toggleExpandida(id: string) {
    setExpandidas(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function renderFactura(e: EntradaCxc) {
    // Prompt: Ajustes finos, ítem 6 — tras cancelar también se puede volver a
    // solicitar (nuevo ciclo, folio nuevo); ver solicitarFactura() en el servicio.
    // Prompt BJ-05B — las 4 acciones de esta función (Solicitar factura,
    // descarga XML/PDF, Cancelar) están gateadas por `puedeEditarCxC`, igual
    // que el botón "Editar": el backend ya las restringe a root/gerente_comercial
    // (guard ESCRIBIR en apps/api/src/routes/cxc/index.ts), pero antes de
    // este fix se mostraban a cualquier rol con acceso de lectura a CxC
    // (head/analista/kam/CEO también las veían y recibían 403 al usarlas).
    if (e.estado_factura === 'solicitar' || e.estado_factura === 'cancelada') {
      if (!puedeEditarCxC) return null;
      return (
        <button className="fact-btn" onClick={() => solicitarMutation.mutate(e.id)} disabled={solicitarMutation.isPending}>
          Solicitar factura
        </button>
      );
    }
    if (e.estado_factura === 'solicitada') {
      return <span className="fact-requested">Factura solicitada</span>;
    }
    if (e.estado_factura === 'emitida') {
      if (!puedeEditarCxC) return null;
      return (
        <div className="fact-icons">
          {e.xml_url && (
            <button className="fact-doc-btn" onClick={() => void handleDescargarFactura(e, 'xml')}>
              XML
            </button>
          )}
          {e.pdf_url && (
            <button className="fact-doc-btn" onClick={() => void handleDescargarFactura(e, 'pdf')}>
              PDF
            </button>
          )}
          <span style={{ color: 'var(--border)', margin: '0 2px' }}>|</span>
          <button className="fact-cancel" onClick={() => handleCancelar(e.id)} disabled={cancelarMutation.isPending}>
            Cancelar
          </button>
        </div>
      );
    }
    return <Badge tone="neutral">Cancelada</Badge>;
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 19, fontWeight: 700, color: 'var(--heading)' }}>Cuentas por Cobrar</div>
          <div style={{ fontSize: 12.5, color: 'var(--muted)', marginTop: 3 }}>Gestión de facturas y seguimiento de cobranza</div>
        </div>
        {puedeEditarCxC && <Button onClick={() => setEntradaModal({ modo: 'crear' })}>Nueva Entrada</Button>}
      </div>

      <div className="stats-grid stats-grid-5">
        <div className="stat-card hl">
          <div className="stat-label">Facturado</div>
          <div className="stat-value">{fmtMXN(stats.totalFacturado)}</div>
          <div className="stat-sub">{stats.facturasActivas} facturas activas · del periodo filtrado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Deuda Vencida</div>
          <div className="stat-value danger">{fmtMXN(stats.deudaVencida)}</div>
          <div className="stat-sub">{stats.facturasEnMora} facturas en mora · al día de hoy, todos los meses</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Por cobrar</div>
          <div className="stat-value">{fmtMXN(stats.porCobrar)}</div>
          <div className="stat-sub">{stats.facturasPorCobrar} sin pagar · del periodo filtrado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Cobrado</div>
          <div className="stat-value success">{fmtMXN(stats.cobrado)}</div>
          <div className="stat-sub">{stats.facturasLiquidadas} facturas pagadas · del periodo filtrado</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Por vencer (30 días)</div>
          <div className="stat-value">{fmtMXN(stats.porVencer)}</div>
          <div className="stat-sub">{stats.facturasPorVencer} facturas vigentes · al día de hoy, todos los meses</div>
        </div>
      </div>

      {accionError && <div className="form-error-banner">{accionError}</div>}

      <div className="filters-bar">
        <input
          className="filter-input filter-search"
          type="text"
          placeholder="Buscar cliente, factura..."
          value={search}
          onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <div className="cxc-filtro">
          <CheckboxDropdown
            options={aniosDisponibles.map(a => ({ value: String(a), label: String(a) }))}
            selected={aniosAplicados}
            onChange={v => { setAnioFiltro(v); setPage(1); }}
            placeholder="Todos los años"
          />
        </div>
        <div className="cxc-filtro">
          <CheckboxDropdown
            options={MESES.map(m => ({ value: m, label: m }))}
            selected={mesesAplicados}
            onChange={v => { setMesFiltro(v); setPage(1); }}
            placeholder="Todos los meses"
          />
        </div>
        <div className="cxc-filtro">
          <CheckboxDropdown
            options={ESTADO_FILTER_OPTIONS}
            selected={estadoFiltro}
            onChange={v => { setEstadoFiltro(v); setPage(1); }}
            placeholder="Estado"
          />
        </div>
        <div className="cxc-filtro cxc-filtro-cliente">
          <CheckboxDropdown
            options={(clientesData?.data ?? []).map(c => ({ value: c.id, label: c.nombre_comercial }))}
            selected={clienteFiltro}
            onChange={v => { setClienteFiltro(v); setPage(1); }}
            placeholder="Todos los clientes"
          />
        </div>
        <div className="cxc-filtro">
          <CheckboxDropdown
            options={TIPO_FILTER_OPTIONS}
            selected={tipoFiltro}
            onChange={v => { setTipoFiltro(v); setPage(1); }}
            placeholder="Servicio y adicionales"
          />
        </div>
        {hasFilters && (
          <button className="filter-reset" onClick={resetFiltros}>
            ✕ Limpiar
          </button>
        )}
      </div>

      <div className="table-wrap">
        <div className="table-scroll">
          <table className="main-table">
            <thead>
              <tr>
                <th>Cliente</th>
                <th>No. Factura</th>
                <th>Mes / Año</th>
                <th>Fecha</th>
                <th>Monto</th>
                <th>Plazo</th>
                <th>Vencimiento</th>
                <th>¿Pagó?</th>
                <th>Estado</th>
                <th>Alerta</th>
                <th>Autorización</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entradasPagina.length === 0 ? (
                <tr>
                  <td className="empty-td" colSpan={12}>
                    {isLoading ? 'Cargando…' : 'No se encontraron registros'}
                  </td>
                </tr>
              ) : (
                entradasPagina.flatMap(e => {
                  const expandida = expandidas.has(e.id);
                  const filas = [
                    <tr key={e.id}>
                      <td style={{ fontWeight: 600 }}>
                        {clienteNombre[e.cliente_id] ?? e.cliente_id}
                        {e.es_adicional && <span className="badge-adicional" title={e.concepto ?? undefined}>Adicional</span>}
                      </td>
                      <td>{renderFactura(e)}</td>
                      <td>
                        {e.mes} {e.anio}
                      </td>
                      <td>{fmtFechaDisplay(e.fecha_factura)}</td>
                      <td style={{ fontWeight: 600 }}>{fmtMXN(e.monto)}</td>
                      <td>{e.plazo_dias} días</td>
                      <td>{fmtFechaDisplay(e.vencimiento)}</td>
                      <td>
                        <PagoIndicator pago={e.pago} />
                      </td>
                      <td>
                        <EstadoBadge entrada={e} />
                      </td>
                      <td>
                        {e.alerta ? (
                          <span className={`alert-pill ${e.alerta_nivel ? ALERTA_CLASS[e.alerta_nivel] : ''}`}>{e.alerta}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td style={{ fontSize: 11.5, color: 'var(--muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {AUTORIZACION_LABEL[e.autorizacion_factura] ?? e.autorizacion_factura}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {puedeEditarCxC && (
                            <button
                              className="td-expand-btn"
                              title="Duplicar al mes siguiente"
                              aria-label="Duplicar al mes siguiente"
                              onClick={() => setEntradaModal({ modo: 'duplicar', prefill: prefillDuplicado(e) })}
                            >
                              <DuplicarIcon />
                            </button>
                          )}
                          {puedeEditarCxC && (
                            <button className="td-expand-btn" onClick={() => setEntradaModal({ modo: 'editar', id: e.id })}>
                              Editar
                            </button>
                          )}
                          <button className="td-expand-btn" onClick={() => toggleExpandida(e.id)}>
                            {expandida ? '▲ Cerrar' : '▼ Detalle'}
                          </button>
                        </div>
                      </td>
                    </tr>,
                  ];

                  if (expandida) {
                    filas.push(
                      <ExpandedRow colSpan={12} key={`${e.id}-exp`}>
                        <ExpField label="Fecha de Pago">{fmtFechaDisplay(e.fecha_pago)}</ExpField>
                        <ExpField label="Días Post-Vcto.">{e.dias_postvencimiento ?? '—'}</ExpField>
                        <ExpField label="Deuda Vencida">{Number(e.deuda_vencida) ? fmtMXN(e.deuda_vencida) : '—'}</ExpField>
                        <ExpField label="Deuda Cobrada">{Number(e.deuda_cobrada) ? fmtMXN(e.deuda_cobrada) : '—'}</ExpField>
                        <ExpField label="Facturado c/IVA">{fmtMXN(e.facturado_con_iva)}</ExpField>
                        <ExpField label="Vencido c/IVA">{Number(e.vencido_con_iva) ? fmtMXN(e.vencido_con_iva) : '—'}</ExpField>
                        <ExpField label="Cobrado c/IVA">{Number(e.cobrado_con_iva) ? fmtMXN(e.cobrado_con_iva) : '—'}</ExpField>
                        <ExpField label="Promesa de pago">{fmtFechaDisplay(e.promesa_pago)}</ExpField>
                        <ExpField label="Concepto">{e.concepto || '—'}</ExpField>
                        {e.folio_solicitud && <ExpField label="Folio de Solicitud">{e.folio_solicitud}</ExpField>}
                        <ExpField label="Plan de Acción" gridColumn="1 / 3">{e.plan_accion || '—'}</ExpField>
                        <ExpField label="Comentarios" gridColumn="3 / 5">{e.comentarios || '—'}</ExpField>
                      </ExpandedRow>,
                    );
                  }

                  return filas;
                })
              )}
            </tbody>
          </table>
        </div>
        <div className="table-footer">
          <span className="table-count">
            {entradasFiltradas.length} registro{entradasFiltradas.length !== 1 ? 's' : ''}
          </span>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>

      <NuevaEntradaModal
        open={entradaModal !== null}
        entradaId={entradaModal?.modo === 'editar' ? entradaModal.id : null}
        prefill={entradaModal?.modo === 'duplicar' ? entradaModal.prefill : null}
        onClose={() => setEntradaModal(null)}
      />

      <ConfirmModal
        open={cancelarPendiente !== null}
        title="¿Cancelar esta factura?"
        body="Esta acción no se puede deshacer."
        confirmLabel="Sí, cancelar factura"
        danger
        onConfirm={confirmarCancelar}
        onCancel={() => setCancelarPendiente(null)}
      />
    </div>
  );
}
